import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Globe, QrCode, Receipt, Sparkles, TriangleAlert } from "lucide-react";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, Progress } from "@dishfolio/ui";
import { useOrg } from "../lib/org.tsx";
import { useMenus, useRestaurants } from "../lib/queries.ts";
import { bytes } from "../lib/format.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { MenuCard, NewMenuDialog } from "./menus.tsx";
import { clearPlanIntent, planIntent } from "../lib/intent.ts";
import { PLANS } from "@dishfolio/contracts";

function Meter({ label, used, max, format = String }: { label: string; used: number; max: number; format?: (n: number) => string }) {
  const pct = max > 0 ? (used / max) * 100 : 0;
  return (
    <div className="grid gap-1.5">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="text-muted-foreground">
          {format(used)} / {max >= 100 && label !== "Storage" && label !== "AI credits" ? "∞" : format(max)}
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
}

export function HomePage() {
  const { me, org, can } = useOrg();
  const menus = useMenus();
  const restaurants = useRestaurants();
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState(planIntent);
  const usage = org?.subscription.usage;
  const published = menus.data?.filter((m) => m.status === "published") ?? [];
  return (
    <PageContainer wide>
      <PageHeader eyebrow={org?.organization.name} title={`Hello, ${me.user.name.split(" ")[0]}`} description="Here’s what’s happening with your menus." />
      {intent && org?.plan.key === "free" && can("billing:manage") ? (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
          <Sparkles className="size-4 text-primary" />
          <div className="flex-1">
            <div className="font-medium">Finish upgrading to {PLANS[intent].name}</div>
            <p className="text-muted-foreground">You picked {PLANS[intent].name} on our pricing page. Upgrade now to unlock {intent === "pro" ? "imports, every theme and AI translation" : "table ordering, locations and custom domains"}.</p>
          </div>
          <Button asChild size="sm">
            <Link to="/billing">Upgrade to {PLANS[intent].name}</Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => (clearPlanIntent(), setIntent(null))}>
            Not now
          </Button>
        </div>
      ) : null}
      {org?.subscription.overLimit.length ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <TriangleAlert className="mt-0.5 size-4 text-destructive" />
          <div className="flex-1">
            <div className="font-medium">You’re over your plan limits</div>
            <p className="text-muted-foreground">Nothing was deleted. Extra menus stay online but are read-only until you upgrade or remove some: {org.subscription.overLimit.join(", ")}.</p>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/billing">Review plan</Link>
          </Button>
        </div>
      ) : null}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="grid gap-6 lg:col-span-2">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle>Your menus</CardTitle>
                <CardDescription>
                  {published.length} published · {(menus.data?.length ?? 0) - published.length} draft
                </CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/menus">
                  All menus <ArrowRight />
                </Link>
              </Button>
            </CardHeader>
            {menus.data?.length ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {menus.data.slice(0, 4).map((m) => (
                  <MenuCard key={m.id} menu={m} restaurantName={restaurants.data?.find((r) => r.id === m.restaurantId)?.name} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-start gap-3 rounded-lg bg-accent p-5">
                <div className="font-medium">Create your first menu</div>
                <p className="text-sm text-muted-foreground">Pick a theme, add your dishes, publish and print your QR code.</p>
                {can("menu:edit") ? <Button onClick={() => setOpen(true)}>Create menu</Button> : null}
              </div>
            )}
          </Card>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: <Globe />, title: "Import website", desc: "Turn your site into an editable menu.", to: "/imports/new", search: { type: "website" } },
              { icon: <BookOpen />, title: "Import PDF", desc: "Upload a printed or scanned menu.", to: "/imports/new", search: { type: "pdf" } },
              { icon: <QrCode />, title: "QR codes", desc: "Download codes for tables and windows.", to: "/menus" },
            ].map((a) => (
              <Link key={a.title} to={a.to as never} search={a.search as never} className="group">
                <Card className="h-full gap-2 transition group-hover:border-primary/40">
                  <span className="text-primary [&_svg]:size-5">{a.icon}</span>
                  <div className="font-medium">{a.title}</div>
                  <p className="text-sm text-muted-foreground">{a.desc}</p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
        <div className="grid content-start gap-6">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Plan usage</CardTitle>
              <Badge variant="secondary">{org?.plan.name}</Badge>
            </CardHeader>
            {usage && org ? (
              <div className="grid gap-4">
                <Meter label="Menus" used={usage.menus} max={org.plan.maxMenus} />
                <Meter label="Published" used={usage.publishedMenus} max={org.plan.maxPublishedMenus} />
                <Meter label="Staff accounts" used={usage.members} max={org.plan.maxMembers} />
                <Meter label="Storage" used={usage.storageBytes} max={org.plan.storageBytes} format={bytes} />
                {org.plan.monthlyCredits ? <Meter label="AI credits" used={usage.creditsUsed} max={usage.creditsGranted} /> : null}
              </div>
            ) : null}
            {org?.plan.key !== "business" && can("billing:manage") ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/billing">
                  <Sparkles /> Upgrade
                </Link>
              </Button>
            ) : null}
          </Card>
          {org?.plan.tableOrdering && can("orders:view") ? (
            <Card>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="size-4" /> Table orders
              </CardTitle>
              <p className="text-sm text-muted-foreground">Keep the order board open on a tablet at the pass to get alerts for new orders.</p>
              <Button asChild size="sm">
                <Link to="/orders">Open order board</Link>
              </Button>
            </Card>
          ) : null}
        </div>
      </div>
      <NewMenuDialog open={open} onOpenChange={setOpen} />
    </PageContainer>
  );
}
